import { A2uiComponentEntry, SpaceDocument, SpaceDocumentLimits } from 'types/space';
import { CompositionDefinition, CompositionRef } from 'types/composition';
import { CATALOG, Surface } from '@center-platform/component-catalog';
import { findChildIds } from '../componentGraph';
import { DEFAULT_SPACE_DOCUMENT_LIMITS } from '../limits';
import { expandComposition } from '../expandComposition';
import { validateSpaceDocument } from '../validateSpaceDocument';
import { validateCompositionDefinition } from '../validateCompositionDefinition';
import { ConformanceFinding, ConformanceLevel, makeFinding, spaceErrorToFinding } from './findings';
import { checkContainment, checkSpaceRootKind, LatticeKind } from './containmentLattice';
import { checkCallerContract, checkPrimitivePropSchema, checkSelfContract, checkWireEmitTargets } from './interfaceConformance';
import { checkDataContract, SchemaResolver } from './levels/l3';

/**
 * A composition reference has three distinct resolutions a plain "definition or undefined" lookup
 * (`CompositionResolver`, used by `expandComposition`/`computeLockfile`) cannot tell apart:
 * `'primitive'` (not a composition reference at all — a catalog component), `'resolved'` (a
 * composition version was found), and `'unresolved'` (the name IS a composition reference, but the
 * pinned/latest version could not be found — `REF_UNRESOLVED`). `dbCompositionResolver` (Phase 2)
 * is the production implementation; jest/corpus callers stub this directly.
 */
export type CompositionRefResolution =
  | { status: 'resolved'; version: number; definition: CompositionDefinition }
  | { status: 'unresolved' }
  | { status: 'primitive' };

export type ArtifactCompositionResolver = (ref: CompositionRef) => CompositionRefResolution;

export type VerifiableArtifact =
  | { artifactType: 'space'; document: SpaceDocument }
  | { artifactType: 'composition'; systemName: string; definition: CompositionDefinition };

export interface VerifyArtifactContext {
  resolveComposition: ArtifactCompositionResolver;
  catalogVersion: number;
  /** Defaults to `'a2ui'` — the only surface Space documents/compositions render on today. */
  surface?: Surface;
  /** Schema the artifact's `/sphere/properties/*` bindings resolve against (L3 only); absent = L3 is a no-op even when requested. */
  schemaSystemName?: string;
  resolveSchema?: SchemaResolver;
  limits?: Partial<SpaceDocumentLimits>;
}

export interface VerifyArtifactOptions {
  /** Defaults to L0–L3 — the pure, request-safe levels. L5/L7 (render/differential) are never run here; see `design-system/conformance/renderLevel.ts` (Phase 4). */
  levels?: ConformanceLevel[];
}

export interface VerifyArtifactLevelTiming {
  level: ConformanceLevel;
  durationMs: number;
}

export interface VerifyArtifactResult {
  findings: ConformanceFinding[];
  levelsRun: ConformanceLevel[];
  timings: VerifyArtifactLevelTiming[];
}

const DEFAULT_LEVELS: ConformanceLevel[] = ['L0', 'L1', 'L2', 'L3'];

function refOf(entry: A2uiComponentEntry): CompositionRef {
  return { systemName: entry.component, version: entry.componentVersion as number | undefined };
}

function resolveEntryKind(entry: A2uiComponentEntry, resolveComposition: ArtifactCompositionResolver): LatticeKind | undefined {
  const resolution = resolveComposition(refOf(entry));
  if (resolution.status === 'resolved') return resolution.definition.kind;
  if (resolution.status === 'unresolved') return undefined; // already reported as REF_UNRESOLVED at L1
  return CATALOG.find((catalogEntry) => catalogEntry.type === entry.component)?.role ?? 'primitive';
}

/** L1: every composition reference reachable from `components`, transitively, resolves. Skips into (and past) a reference already flagged `REF_UNRESOLVED` rather than recursing into nothing. */
function collectRefUnresolvedFindings(
  components: readonly A2uiComponentEntry[],
  resolveComposition: ArtifactCompositionResolver,
  path: string,
  chain: readonly string[] = []
): ConformanceFinding[] {
  const findings: ConformanceFinding[] = [];
  for (const entry of components) {
    const resolution = resolveComposition(refOf(entry));
    if (resolution.status === 'unresolved') {
      findings.push(
        makeFinding(
          'REF_UNRESOLVED',
          'L1',
          'error',
          `Reference "${entry.component}" (from component "${entry.id}") does not resolve to a published composition version`,
          { path: `${path}/${entry.id}`, ref: refOf(entry) }
        )
      );
      continue;
    }
    if (resolution.status === 'resolved' && !chain.includes(entry.component)) {
      findings.push(
        ...collectRefUnresolvedFindings(resolution.definition.a2ui.components, resolveComposition, `${path}/${entry.id}`, [...chain, entry.component])
      );
    }
  }
  return findings;
}

/**
 * L2: one traversal producing both containment-lattice findings (every direct parent/child edge,
 * including edges into a referenced composition's own subtree) and per-node contract findings
 * (caller contract for a resolved composition ref; catalog prop schema for a primitive). A cyclic
 * or unresolved reference is skipped, not re-walked — already reported at L1.
 */
function walkScope(
  components: readonly A2uiComponentEntry[],
  root: string,
  resolveComposition: ArtifactCompositionResolver,
  catalogVersion: number,
  surface: Surface,
  chain: readonly string[],
  pathPrefix: string
): ConformanceFinding[] {
  const byId = new Map(components.map((entry) => [entry.id, entry]));
  const knownIds = new Set(byId.keys());
  const visited = new Set<string>();
  const findings: ConformanceFinding[] = [];

  const visit = (id: string): void => {
    if (visited.has(id)) return;
    visited.add(id);
    const entry = byId.get(id);
    if (!entry) return;

    const hostKind = resolveEntryKind(entry, resolveComposition);
    const path = `${pathPrefix}/${id}`;
    const resolution = resolveComposition(refOf(entry));

    if (resolution.status === 'resolved') {
      findings.push(...checkCallerContract(entry, resolution.definition.interface, path));
      if (!chain.includes(entry.component)) {
        findings.push(
          ...walkScope(
            resolution.definition.a2ui.components,
            resolution.definition.a2ui.root,
            resolveComposition,
            catalogVersion,
            surface,
            [...chain, entry.component],
            path
          )
        );
      }
    } else if (resolution.status === 'primitive') {
      findings.push(...checkPrimitivePropSchema(entry, catalogVersion, surface, path));
    }

    for (const childId of findChildIds(entry, knownIds)) {
      const childEntry = byId.get(childId);
      if (!childEntry) continue;
      const childKind = resolveEntryKind(childEntry, resolveComposition);
      if (hostKind && childKind) {
        const containment = checkContainment(hostKind, childKind);
        if (!containment.allowed) {
          findings.push(makeFinding('KIND_CONTAINMENT_VIOLATION', 'L2', 'error', containment.reason ?? 'containment lattice violation', { path: `${pathPrefix}/${childId}` }));
        }
      }
      visit(childId);
    }
  };

  visit(root);
  return findings;
}

/** L3 gathers bindings across every composition reference reachable from `components`, transitively — lattice recursion (`walkScope`) is skipped here since L3 has no containment concept. */
function collectReachableComponents(
  components: readonly A2uiComponentEntry[],
  resolveComposition: ArtifactCompositionResolver,
  chain: readonly string[] = []
): A2uiComponentEntry[] {
  const collected: A2uiComponentEntry[] = [];
  for (const entry of components) {
    collected.push(entry);
    const resolution = resolveComposition(refOf(entry));
    if (resolution.status === 'resolved' && !chain.includes(entry.component)) {
      collected.push(...collectReachableComponents(resolution.definition.a2ui.components, resolveComposition, [...chain, entry.component]));
    }
  }
  return collected;
}

/**
 * The level runner: `verifyArtifact` is the *same function* behind write routes, the composer
 * check, the composer agent's tool result, the jest corpus, the publish gate and the fleet sweep
 * (SC3) — only the injected resolvers and requested `levels` differ per caller. Short-circuits on
 * the first level carrying an `error`-severity finding (a structurally broken document never
 * reaches expansion; an unresolved reference never reaches contract checking).
 */
export function verifyArtifact(
  artifact: VerifiableArtifact,
  ctx: VerifyArtifactContext,
  options: VerifyArtifactOptions = {}
): VerifyArtifactResult {
  const levels = new Set(options.levels ?? DEFAULT_LEVELS);
  const surface: Surface = ctx.surface ?? 'a2ui';
  const findings: ConformanceFinding[] = [];
  const levelsRun: ConformanceLevel[] = [];
  const timings: VerifyArtifactLevelTiming[] = [];
  const a2ui = artifact.artifactType === 'space' ? artifact.document.a2ui : artifact.definition.a2ui;

  const runLevel = (level: ConformanceLevel, fn: () => ConformanceFinding[]): ConformanceFinding[] => {
    const start = performance.now();
    const levelFindings = fn();
    timings.push({ level, durationMs: performance.now() - start });
    levelsRun.push(level);
    findings.push(...levelFindings);
    return levelFindings;
  };

  if (levels.has('L0')) {
    const l0Errors =
      artifact.artifactType === 'space'
        ? validateSpaceDocument(artifact.document, ctx.limits).errors
        : validateCompositionDefinition(artifact.definition, { ...DEFAULT_SPACE_DOCUMENT_LIMITS, ...ctx.limits }).errors;
    const l0Findings = runLevel('L0', () => l0Errors.map((error) => spaceErrorToFinding(error, 'L0')));
    if (l0Findings.some((f) => f.severity === 'error')) {
      return { findings, levelsRun, timings };
    }
  }

  if (levels.has('L1')) {
    const l1Findings = runLevel('L1', () => {
      const adaptedResolver = (ref: CompositionRef): CompositionDefinition | undefined => {
        const resolution = ctx.resolveComposition(ref);
        return resolution.status === 'resolved' ? resolution.definition : undefined;
      };
      const expansionErrors = expandComposition(a2ui, adaptedResolver, ctx.limits).errors;
      return [
        ...expansionErrors.map((error) => spaceErrorToFinding(error, 'L1')),
        ...collectRefUnresolvedFindings(a2ui.components, ctx.resolveComposition, 'a2ui/components'),
      ];
    });
    if (l1Findings.some((f) => f.severity === 'error')) {
      return { findings, levelsRun, timings };
    }
  }

  if (levels.has('L2')) {
    runLevel('L2', () => {
      const l2Findings = walkScope(a2ui.components, a2ui.root, ctx.resolveComposition, ctx.catalogVersion, surface, [], 'a2ui/components');

      if (artifact.artifactType === 'space') {
        const rootEntry = a2ui.components.find((entry) => entry.id === a2ui.root);
        if (rootEntry) {
          const rootKind = resolveEntryKind(rootEntry, ctx.resolveComposition);
          if (rootKind) {
            const rootCheck = checkSpaceRootKind(rootKind);
            if (!rootCheck.allowed) {
              l2Findings.push(makeFinding('KIND_CONTAINMENT_VIOLATION', 'L2', 'error', rootCheck.reason ?? 'a2ui.root kind violation', { path: 'a2ui/root' }));
            }
          }
        }

        const entriesById = new Map(a2ui.components.map((entry) => [entry.id, entry]));
        const resolveInterface = (ref: CompositionRef) => {
          const resolution = ctx.resolveComposition(ref);
          return resolution.status === 'resolved' ? resolution.definition.interface : undefined;
        };
        l2Findings.push(...checkWireEmitTargets(artifact.document.wires ?? [], entriesById, resolveInterface, 'wires'));
      } else {
        l2Findings.push(...checkSelfContract(artifact.definition, artifact.systemName));
      }

      return l2Findings;
    });
  }

  if (levels.has('L3')) {
    runLevel('L3', () =>
      checkDataContract(
        collectReachableComponents(a2ui.components, ctx.resolveComposition),
        ctx.schemaSystemName,
        ctx.resolveSchema ?? (() => undefined),
        'a2ui/components'
      )
    );
  }

  return { findings, levelsRun, timings };
}
